import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Heading } from '../../atoms';
import { useLanguage } from '../../../context/LanguageContext';
import './ServiceCard.css';

function ServiceCard({ title, description, icon }) {
  const { t } = useLanguage();
  const dialogTitleId = useId();
  const dialogId = useId();
  const descriptionRef = useRef(null);
  const closeBtnRef = useRef(null);
  const dialogRef = useRef(null);
  const openBtnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const readMoreLabel = t('services.readMore');
  const closeLabel = t('services.closeModal');

  const updateTruncation = useCallback(() => {
    const el = descriptionRef.current;
    if (!el) return;
    const next = el.scrollHeight > el.clientHeight + 2;
    setIsTruncated(next);
  }, []);

  useLayoutEffect(() => {
    updateTruncation();
    const el = descriptionRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => updateTruncation());
    ro.observe(el);
    return () => ro.disconnect();
  }, [description, updateTruncation]);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const tClose = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => window.clearTimeout(tClose);
  }, [open]);

  const handleClose = useCallback(() => {
    setOpen(false);
    window.setTimeout(() => openBtnRef.current?.focus(), 0);
  }, []);

  const handleDialogKeyDown = (e) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const modal =
    open &&
    createPortal(
      <div className="service-card-modal" role="presentation">
        <div
          className="service-card-modal__backdrop"
          onClick={handleClose}
          aria-hidden="true"
        />
        <div
          id={dialogId}
          ref={dialogRef}
          className="service-card-modal__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialogTitleId}
          onKeyDown={handleDialogKeyDown}
        >
          <button
            ref={closeBtnRef}
            type="button"
            className="service-card-modal__close"
            onClick={handleClose}
            aria-label={closeLabel}
          >
            <span className="service-card-modal__close-icon" aria-hidden="true">
              ×
            </span>
          </button>
          {icon && (
            <div className="service-card-modal__icon" aria-hidden>
              {icon}
            </div>
          )}
          <h2 id={dialogTitleId} className="service-card-modal__title">
            {title}
          </h2>
          <div className="service-card-modal__scroll">
            <p className="service-card-modal__text">{description}</p>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <article className="service-card">
        {icon && <div className="service-card__icon" aria-hidden>{icon}</div>}
        <Heading level={3} className="service-card__title">
          {title}
        </Heading>
        <div className="service-card__description-wrap">
          <p ref={descriptionRef} className="service-card__description">
            {description}
          </p>
        </div>
        <div className="service-card__actions">
          {isTruncated ? (
            <button
              ref={openBtnRef}
              type="button"
              className="service-card__read-more"
              onClick={() => setOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-controls={open ? dialogId : undefined}
            >
              {readMoreLabel}
            </button>
          ) : null}
        </div>
      </article>
      {modal}
    </>
  );
}

export default ServiceCard;
